/**
 * `cusa-petition-sign-state.js`
 *
 * Petition/Pledge-only behavior.
 *
 * Watches for `guardianDataSet` (PUB/SUB) and updates the petition sign UI based on
 * `window.guardianData.petitions`.
 *
 * Expected DOM hooks:
 * - `#petition` with `data-handle` (string) and optional `data-type` (e.g. "petition" | "pledge")
 * - `#petition-sign` (button/link) which may start disabled via `.disabled`
 * - `#petition-sign-fieldset` (fieldset) which may start disabled via `disabled` attribute
 * - `[data-cusa-sign-state-msg]` (container) where copy/spinner is shown
 *   - Rendered in the sign form area and (optionally) a duplicate in the content column for mobile visibility.
 */

/**
 * @typedef {Object} GuardianPetition
 * @property {string} handle
 * @property {string} [date_signed]
 */

/**
 * @typedef {Object} GuardianData
 * @property {GuardianPetition[]} [petitions]
 */

/**
 * @typedef {Object} PubSubEvents
 * @property {string} [guardianDataSet]
 */

/**
 * @typedef {Object} CusaPetitionSignStateWindow
 * @property {boolean} [__cusaPetitionSignStateInit]
 * @property {GuardianData} [guardianData]
 * @property {PubSubEvents} [PUB_SUB_EVENTS]
 * @property {(eventName: string, callback: () => void) => void} [subscribe]
 */

/**
 * Initializes petition/pledge sign-state UI.
 *
 * This module is intended to be loaded only on petition/pledge pages.
 * It is safe to include multiple times because it guards against double init.
 *
 * @returns {void}
 */
(function initCusaPetitionSignState() {
	/** @type {CusaPetitionSignStateWindow} */
	// eslint-disable-next-line no-undef
	const w = /** @type {any} */ (window);

	// Guard: ensure this script only initializes once per page load.
	if (w.__cusaPetitionSignStateInit) return;
	w.__cusaPetitionSignStateInit = true;

	/** @type {HTMLElement|null} */
	let petitionEl = document.querySelector('#petition');
	/** @type {HTMLElement|null} */
	let formSection = document.querySelector('#petition-sign');
	/** @type {HTMLFieldSetElement|null} */
	let fieldset = /** @type {HTMLFieldSetElement|null} */ (document.querySelector('#petition-sign-fieldset'));
	/** @type {HTMLElement[]} */
	let msgEls = Array.from(document.querySelectorAll('[data-cusa-sign-state-msg]'));

	// If this page doesn't have the petition UI, do nothing.
	if (!petitionEl || !formSection || !fieldset) return;

	/** @type {string} */
	let handle = petitionEl?.dataset?.handle || '';
	/** @type {string} */
	let type = petitionEl?.dataset?.type || 'petition';

	if (!handle) return;

	/**
	 * Returns true when the sign UI appears to be disabled.
	 * @returns {boolean}
	 */
	const isDisabled = () => {
		let formDisabled = formSection?.classList.contains('disabled');
		let fieldsetDisabled = fieldset?.hasAttribute('disabled') || fieldset?.dataset?.disabled;
		return !!(formDisabled || fieldsetDisabled);
	};

	/**
	 * Enables the sign controls.
	 * @returns {void}
	 */
	const enableSign = () => {
		formSection?.classList.remove('disabled');
		fieldset?.removeAttribute('disabled');
		if (fieldset?.dataset) delete fieldset.dataset.disabled;
	};

	/**
	 * Disables the sign controls.
	 * @returns {void}
	 */
	const disableSign = () => {
		formSection?.classList.add('disabled');
		fieldset?.setAttribute('disabled', 'disabled');
	};

	/**
	 * Sets the status message text.
	 * @param {string} text
	 * @returns {void}
	 */
	const setMsg = (text) => {
		if (!msgEls.length) return;
		msgEls.forEach((el) => {
			el.innerHTML = text;
		});
	};

	/**
	 * Hides the spinner inside the status message area (if present).
	 * @param {HTMLElement} el
	 * @returns {void}
	 */
	const hideSpinner = (el) => {
		el?.querySelector('.loading__spinner')?.classList.add('hidden');
	};

	/**
	 * Hides the entire status message area.
	 * @returns {void}
	 */
	const hideMsg = () => {
		msgEls.forEach((el) => {
			el.textContent = '';
			el.hidden = true;
			el.setAttribute('aria-hidden', 'true');
			hideSpinner(el);
		});
	};

	/**
	 * Shows the entire status message area.
	 * @returns {void}
	 */
	const showMsg = () => {
		msgEls.forEach((el) => {
			el.hidden = false;
			el.setAttribute('aria-hidden', 'false');
		});
	};

	/**
	 * Applies signed state based on the current Guardian payload.
	 *
	 * If the UI is already enabled, this is a cheap no-op (prevents redundant DOM work
	 * when `guardianDataSet` fires multiple times).
	 *
	 * @param {boolean=} alreadySignedRes
	 * @returns {void}
	 */
	const applySignatureState = (alreadySignedRes) => {
		// If the UI is already enabled and not marked disabled, no need to re-run.
		// (Keeps this cheap when `guardianDataSet` fires multiple times.)
		// Still hide the spinner in case it was left visible by initial markup.
		const forceApply = alreadySignedRes === true;
	if (!isDisabled() && !forceApply) {
		hideMsg();
		return;
	}
	let petitions = w.guardianData?.petitions;
	if (!Array.isArray(petitions) && !forceApply) {
		showMsg();
		return;
	}

		let matchingPetition = petitions?.find((p) => p && p.handle === handle);
		let alreadySigned = alreadySignedRes || !!matchingPetition;
	if (alreadySigned) {
		disableSign();

			let date = matchingPetition?.date_signed ? new Date(matchingPetition.date_signed) : null;
			let dateStr = date ? date.toLocaleDateString() : '';
			if (date)
				setMsg(
					`<span class='already-signed-text'>You have already signed this ${type}${dateStr ? ` on ${dateStr}` : ''}. Thank you for your support!</span>`
				);
			else {
				setMsg(
					`<span class='already-signed-text'>You have already signed this ${type}. Thank you for your support!</span>`
				);
			}
		showMsg();
			// Only an arrival-time determination from Guardian data is a genuine
			// `direct_logged_in` entry. When forced (the 409 re-check), errorStateUpdate
			// reports the entry itself as `after_duplicate_signature` — passing no reason
			// here keeps the duplicate from being mislabelled as a direct arrival.
			signedStateUpdate(formSection.querySelector('form'), undefined, forceApply ? undefined : 'direct_logged_in');
			return;
		}

		// Not signed: enable and hide message area.
		enableSign();
		hideMsg();
	};

	// Expose the helper so other scripts can re-check signed state (e.g., after a 409).
	if (!w.applySignatureState) {
		w.applySignatureState = applySignatureState;
	}

	// Initial pass in case guardian data is already available.
	applySignatureState();

	// Preferred: pub/sub signal from the existing app code.
	// Some builds expose these on `window`, others keep them as globals in the theme bundle.
	/** @type {((eventName: string, callback: () => void) => void)|null} */
	const subscribeFn =
		typeof w.subscribe === 'function' ? w.subscribe : typeof subscribe !== 'undefined' ? subscribe : null;

	/** @type {PubSubEvents|null} */
	const pubSubEvents = w.PUB_SUB_EVENTS || (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null);

	if (typeof subscribeFn === 'function' && pubSubEvents?.guardianDataSet) {
		subscribeFn(pubSubEvents.guardianDataSet, applySignatureState);
		return;
	}

	/**
	 * Fallback event-based integration when the pub/sub helper is not present.
	 * @type {(ev: Event) => void}
	 */
	const onGuardianDataSet = () => applySignatureState();
	w.addEventListener('guardianDataSet', onGuardianDataSet);
})();
